import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { ActionContext } from "@/components/DatasetVersionHistory/common";
import { DeleteAction } from "@/components/DatasetVersionHistory/DeleteAction";
import { EditAction } from "@/components/DatasetVersionHistory/EditAction";
import DatasetVersionPublishButton from "@/components/DatasetVersionPublishButton";
import EntityProfilePic from "@/components/EntityProfilePic";
import { FormattedDataSize } from "@/components/FormattedDataSize";
import {
  CalendarIcon,
  DataIcon,
  DotsVerticalIcon,
  Download02Icon,
} from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import { Popover, PopoverContent, PopoverTrigger } from "@/kit/ui/popover";
import { useMediaQuery } from "@/utils/hooks";
import { cn } from "@/utils/tailwind";
import { GoDotFill } from "react-icons/go";
import { LuDot } from "react-icons/lu";
import { FormattedDate, FormattedMessage, FormattedNumber } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { generatePath } from "react-router";
import { CardVersionHistoryFragment$key } from "./__generated__/CardVersionHistoryFragment.graphql";
import { splitDatasetSlug } from "@/utils/routing";

const CardVersionHistoryFragment = graphql`
  fragment CardVersionHistoryFragment on DatasetVersion {
    id
    version
    dataset {
      id
      slug
      owner {
        username
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }
    analytics {
      downloadCount
      fileSize
    }
    updatedAt
    publishedAt
    doi {
      full
    }
    viewerCanDeleteDatasetVersion: viewerCan(action: DELETE_DATASET_VERSION)
    viewerCanUpdateDatasetVersion: viewerCan(action: UPDATE_DATASET_VERSION)

    ...DatasetVersionPublishButtonFragment
  }
`;

interface Props {
  datasetVersion: CardVersionHistoryFragment$key;
}

export function Card({ datasetVersion: fragment }: Props) {
  const datasetVersion = useFragment(CardVersionHistoryFragment, fragment);
  const {
    id,
    version,
    dataset: { owner, slug },
    updatedAt,
    publishedAt,
    analytics,
  } = datasetVersion;
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  const canDelete =
    datasetVersion.viewerCanDeleteDatasetVersion && !datasetVersion.doi;
  const canUpdate = datasetVersion.viewerCanUpdateDatasetVersion;

  const canSeeActions = canDelete || canUpdate;

  return (
    <div className="w-full p-4 flex flex-row justify-between sm:items-center items-baseline rounded-lg border border-grey-200 transition-colors hover:bg-linear-to-r hover:from-slate-50 hover:to-white">
      <div className="flex flex-col items-start gap-2">
        <Link
          to={generatePath("/datasets/:owner/:slug/versions/:version", {
            ...splitDatasetSlug(slug),
            version,
          })}
          hue="grey"
          className="hover:text-brand-500"
        >
          <FormattedMessage
            defaultMessage="Version {version}"
            values={{ version }}
          />
        </Link>
        <div className="flex items-center flex-row">
          <div className="flex flex-row items-center gap-2">
            <EntityProfilePic size="6" entity={owner} />
            <p className="text-xs font-medium text-grey-600 leading-4">
              {owner.username}
            </p>
          </div>
          <Seperator />
          <div className="flex flex-row items-center gap-2 text-grey-500">
            <CalendarIcon className="w-5 h-5" />
            <p className="text-xs font-normal text-grey-600 leading-4 line-clamp-3">
              <FormattedMessage
                defaultMessage="Edited {date}"
                values={{
                  date: (
                    <FormattedDate
                      value={updatedAt}
                      month="short"
                      day="numeric"
                      year="numeric"
                    />
                  ),
                }}
              />
            </p>
          </div>
          <Seperator className="sm:block hidden" />
          <div className="hidden sm:flex flex-row items-center gap-2 text-grey-500">
            <Download02Icon className="w-5 h-5" />
            <p className="text-xs font-normal text-grey-600 leading-4 line-clamp-3">
              <FormattedNumber
                value={analytics.downloadCount}
                notation="compact"
              />
            </p>
          </div>
          <Seperator className="sm:block hidden" />
          <div className="hidden sm:flex flex-row items-center gap-2 text-grey-500">
            <DataIcon className="w-5 h-5" />
            <p className="text-xs font-normal text-grey-600 leading-4 line-clamp-3">
              <FormattedDataSize bytes={analytics.fileSize} />
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center gap-2">
        {publishedAt ? (
          <div>
            <Badge variant="success">
              <GoDotFill />
              <FormattedMessage defaultMessage="Active" />
            </Badge>
          </div>
        ) : (
          <DatasetVersionPublishButton
            datasetVersion={datasetVersion}
            variant={isDesktop ? "secondary" : "link"}
            className="w-full"
          >
            <FormattedMessage defaultMessage="Publish" />
          </DatasetVersionPublishButton>
        )}

        {canSeeActions && (
          <ActionContext.Provider
            value={{
              id,
              version: datasetVersion.version,
              datasetId: datasetVersion.dataset.id,
            }}
          >
            <DatasetVersionAction
              viewerCanDeleteDatasetVersion={canDelete}
              viewerCanUpdateDatasetVersion={canUpdate}
            />
          </ActionContext.Provider>
        )}
      </div>
    </div>
  );
}

interface DatasetVersionActionProps {
  viewerCanDeleteDatasetVersion: boolean;
  viewerCanUpdateDatasetVersion: boolean;
}

function DatasetVersionAction(props: DatasetVersionActionProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="tertiary" hue="grey" size="md" icon>
          <DotsVerticalIcon className="w-5 h-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-48" align="end" alignOffset={10}>
        {props.viewerCanUpdateDatasetVersion && (
          <>
            <EditAction />
            <hr className="text-grey-200" />
          </>
        )}
        {props.viewerCanDeleteDatasetVersion && <DeleteAction />}
      </PopoverContent>
    </Popover>
  );
}

function Seperator({ className }: { className?: string }) {
  return (
    <span className={cn("px-1", className)}>
      <LuDot className="text-grey-300 h-8" />
    </span>
  );
}
